import React from 'react';
import { hashCode } from './String';
import useMap from './useMap';
import { Palette } from './Palette';
const createPath = (path, map, highlight) => new google.maps.Polyline({
    path: path.map((e) => ({ lat: e.lat, lng: e.lng })),
    geodesic: true,
    strokeColor: highlight ? Palette.HighlightPath : Palette.Path,
    strokeOpacity: highlight ? 1.0 : 0.25,
    strokeWeight: 5,
    map,
});
const createFootprint = (point, map, highlight) => new google.maps.Circle({
    center: { lat: point.lat, lng: point.lng },
    strokeColor: highlight ? Palette.HighlightPath : Palette.Path,
    strokeOpacity: 0.75,
    strokeWeight: 3,
    fillColor: highlight ? Palette.HighlightPath : Palette.Path,
    fillOpacity: 0.75,
    radius: 1,
    map,
});
function usePaths(paths, highlightPath) {
    const { map } = useMap();
    const ref = React.useRef(new Map());
    const footprintRef = React.useRef(new Map());
    React.useEffect(() => {
        if (!map) {
            return;
        }
        const oldKeys = new Set([...ref.current.keys()]);
        const newKeys = new Map(paths?.map((e) => [hashCode(JSON.stringify(e || '')), e]) || []);
        [...oldKeys]
            .filter((e) => !newKeys.has(e) && ref.current.has(e))
            .forEach((e) => {
            ref.current?.get(e)?.setMap(null);
            ref.current?.delete(e);
        });
        [...newKeys.entries()]
            .filter((e) => !oldKeys.has(e[0]) || !ref.current.has(e[0]))
            .forEach((e) => {
            const key = e[0];
            const object = e[1];
            const newObject = createPath(object, map, highlightPath);
            ref.current.set(key, newObject);
        });
        const oldFootprintKeys = new Set([...footprintRef.current.keys()]);
        const newFootprintKeys = new Map(paths?.flatMap((path) => path.map((e) => [hashCode(JSON.stringify(e || '')), e])));
        [...oldFootprintKeys]
            .filter((e) => !newFootprintKeys.has(e) && footprintRef.current.has(e))
            .forEach((e) => {
            footprintRef.current?.get(e)?.setMap(null);
            footprintRef.current?.delete(e);
        });
        [...newFootprintKeys.entries()]
            .filter((e) => !oldFootprintKeys.has(e[0]) || !footprintRef.current.has(e[0]))
            .forEach((e) => {
            const key = e[0];
            const object = e[1];
            const newObject = createFootprint(object, map, highlightPath);
            footprintRef.current.set(key, newObject);
        });
    }, [paths, ref, footprintRef, map, highlightPath]);
    return {};
}
export default usePaths;
